import { useState, useMemo } from 'react';
import {
  addDays, addMonths, addWeeks, eachDayOfInterval, endOfMonth, endOfWeek, format,
  isSameDay, isSameMonth, isToday, startOfMonth, startOfWeek, subMonths, subWeeks,
} from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  Users,
  CheckSquare,
  Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { meetings as mockMeetings } from '@/data/mockData';
import { useDashboardData, useSyncedMeetings, useSyncedProjects, normalizeHealth } from '@/hooks/useSyncedData';
import { useTenant } from '@/hooks/useTenant';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

// Import avatars
import sarahChenAvatar from '@/assets/inbox/sarah-chen-email.png';
import michaelTorresAvatar from '@/assets/inbox/michael-torres.png';
import alexKimAvatar from '@/assets/inbox/alex-kim.png';
import emilyWatsonAvatar from '@/assets/inbox/emily-watson.png';
import { useLocalization } from '@/hooks/useLocalization';

const attendeeAvatars: Record<string, string> = {
  'Sarah Chen': sarahChenAvatar,
  'Michael Torres': michaelTorresAvatar,
  'Alex Kim': alexKimAvatar,
  'Emily Watson': emilyWatsonAvatar,
};

type ViewMode = 'month' | 'week';

// Status drives colour, using the design-system semantic tokens:
// delayed -> destructive (red), in progress / at risk -> warning (amber),
// on track -> success (green), meetings -> primary.
type EventStatus = 'delayed' | 'in-progress' | 'on-track' | 'done' | 'meeting';

const STATUS_STYLE: Record<EventStatus, { dot: string; chip: string; label: string; badge: string }> = {
  delayed: { dot: 'bg-destructive', chip: 'border-destructive bg-destructive/10', label: 'Delayed', badge: 'bg-destructive/10 text-destructive' },
  'in-progress': { dot: 'bg-warning', chip: 'border-warning bg-warning/15', label: 'In progress', badge: 'bg-warning/15 text-warning-foreground' },
  'on-track': { dot: 'bg-success', chip: 'border-success bg-success/10', label: 'On track', badge: 'bg-success/10 text-success' },
  done: { dot: 'bg-success', chip: 'border-success/60 bg-success/5 opacity-70', label: 'Completed', badge: 'bg-success/10 text-success' },
  meeting: { dot: 'bg-primary', chip: 'border-primary bg-primary/10', label: 'Meeting', badge: 'bg-primary/10 text-primary' },
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

function taskStatus(status: string, due: Date): EventStatus {
  if (status === 'done') return 'done';
  if (due < startOfToday()) return 'delayed';
  if (status === 'in-progress' || status === 'review') return 'in-progress';
  return 'on-track';
}

function milestoneStatus(status: string, due: Date): EventStatus {
  if (status === 'completed') return 'done';
  if (status === 'delayed' || due < startOfToday()) return 'delayed';
  if (status === 'in-progress') return 'in-progress';
  return 'on-track';
}

const HEALTH_STATUS: Record<string, EventStatus> = { critical: 'delayed', 'at-risk': 'in-progress', 'on-track': 'on-track' };

interface CalendarEvent {
  id: string;
  title: string;
  date: Date;
  endDate?: Date;
  type: 'meeting' | 'task' | 'milestone' | 'deadline';
  status: EventStatus;
  color: string;
  attendees?: string[];
  duration?: string;
  project?: string;
}

const CalendarView = () => {
  const { tx, t } = useLocalization();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [userEvents, setUserEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { toast } = useToast();
  const { isDemoTenant } = useTenant();

  // Real synced tasks, milestones and project deadlines (demo tenants fall back
  // to mock data inside useDashboardData); meetings come from meetings_sync.
  const { projects, tasks } = useDashboardData();
  const { data: syncedProjects } = useSyncedProjects();
  const { data: syncedMeetings } = useSyncedMeetings();

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];
    const push = (e: Omit<CalendarEvent, 'color'>) => events.push({ ...e, color: STATUS_STYLE[e.status].dot });
    const valid = (d: Date) => !Number.isNaN(d.getTime());

    const meetings = syncedMeetings?.length
      ? syncedMeetings.map((m) => ({ id: m.external_id || m.id, title: m.title, date: new Date(m.start_time), attendees: undefined as string[] | undefined, duration: undefined as string | undefined }))
      : isDemoTenant
        ? mockMeetings.map((m) => ({ id: m.id, title: m.title, date: new Date(m.date), attendees: m.attendees, duration: m.duration }))
        : [];
    meetings.forEach((m) => {
      if (valid(m.date)) push({ id: `meeting-${m.id}`, title: m.title, date: m.date, type: 'meeting', status: 'meeting', attendees: m.attendees, duration: m.duration });
    });

    tasks.forEach((task) => {
      const date = new Date(task.dueDate);
      if (valid(date)) push({ id: `task-${task.id}`, title: task.title, date, type: 'task', status: taskStatus(task.status, date), project: task.project });
    });

    projects.forEach((project) => {
      project.milestones.forEach((ms) => {
        const date = new Date(ms.endDate);
        if (valid(date)) push({ id: `milestone-${project.id}-${ms.id}`, title: `${project.name}: ${ms.name}`, date, type: 'milestone', status: milestoneStatus(ms.status, date), project: project.name });
      });
    });

    // Project deadlines (only where the source actually supplied an end date).
    const deadlineSource = syncedProjects?.length
      ? syncedProjects.filter((p) => p.end_date).map((p) => ({ id: p.external_id, name: p.name, end: p.end_date as string, health: normalizeHealth(p.health), progress: p.progress }))
      : projects.map((p) => ({ id: p.id, name: p.name, end: p.endDate, health: p.health, progress: p.progress }));
    deadlineSource.forEach((p) => {
      const date = new Date(p.end);
      if (!valid(date)) return;
      const status: EventStatus = p.progress >= 100 ? 'done' : date < startOfToday() ? 'delayed' : HEALTH_STATUS[p.health] ?? 'on-track';
      push({ id: `deadline-${p.id}`, title: `${p.name}: deadline`, date, type: 'deadline', status, project: p.name });
    });

    return [...events, ...userEvents];
  }, [syncedMeetings, syncedProjects, tasks, projects, isDemoTenant, userEvents]);

  const getEventsForDate = (date: Date) => calendarEvents.filter((e) => isSameDay(new Date(e.date), date));

  const goToPrevious = () =>
    setCurrentDate(viewMode === 'month' ? subMonths(currentDate, 1) : subWeeks(currentDate, 1));
  const goToNext = () =>
    setCurrentDate(viewMode === 'month' ? addMonths(currentDate, 1) : addWeeks(currentDate, 1));
  const goToToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDate(now);
  };

  // Whole weeks only, so the grid never shows blank filler blocks.
  const days = useMemo(() => {
    if (viewMode === 'week') {
      const start = startOfWeek(currentDate);
      return eachDayOfInterval({ start, end: addDays(start, 6) });
    }
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(currentDate)),
      end: endOfWeek(endOfMonth(currentDate)),
    });
  }, [currentDate, viewMode]);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const maxChips = viewMode === 'week' ? 8 : 2;

  const title =
    viewMode === 'month'
      ? format(currentDate, 'MMMM yyyy')
      : `${format(days[0], 'MMM d')} – ${format(days[6], 'MMM d, yyyy')}`;

  const selectedEvents = getEventsForDate(selectedDate);
  const upcoming = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = addDays(from, 8);
    return calendarEvents
      .filter((e) => new Date(e.date) >= from && new Date(e.date) < to && !isSameDay(new Date(e.date), selectedDate))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 6);
  }, [calendarEvents, selectedDate]);

  const handleAddEvent = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const type = (fd.get('type') as CalendarEvent['type']) || 'meeting';
    const [y, m, d] = String(fd.get('date')).split('-').map(Number);
    if (!y || !m || !d) return;
    const date = new Date(y, m - 1, d);
    setUserEvents((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        title: String(fd.get('title')),
        date,
        type,
        status: type === 'meeting' ? 'meeting' : date < startOfToday() ? 'delayed' : 'on-track',
        color: type === 'meeting' ? 'bg-primary' : date < startOfToday() ? 'bg-destructive' : 'bg-success',
      },
    ]);
    setCurrentDate(date);
    setSelectedDate(date);
    toast({
      title: t('Event Created'),
      description: t('"{0}" has been added to your calendar.', { 0: String(fd.get('title') ?? '') }),
    });
    setIsAddDialogOpen(false);
  };

  const typeIcon = (t: CalendarEvent['type']) =>
    t === 'task' ? <CheckSquare className="w-3 h-3" /> : t === 'milestone' || t === 'deadline' ? <Target className="w-3 h-3" /> : <CalendarIcon className="w-3 h-3" />;

  const legend: { s: EventStatus; l: string }[] = [
    { s: 'delayed', l: 'Delayed' },
    { s: 'in-progress', l: 'In progress / at risk' },
    { s: 'on-track', l: 'On track' },
    { s: 'done', l: 'Completed' },
    { s: 'meeting', l: 'Meeting' },
  ];

  const AgendaRow = ({ event }: { event: CalendarEvent }) => {
  const { tx } = useLocalization();
  return (
    <button
      onClick={() => setSelectedEvent(event)}
      className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-start hover:bg-secondary/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className={cn('w-1 self-stretch rounded-full', event.color)} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground truncate">{tx(event.title)}</span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          {typeIcon(event.type)}
          <span className={cn('rounded px-1.5 py-px font-medium', STATUS_STYLE[event.status].badge)}>
            {tx(STATUS_STYLE[event.status].label)}
          </span>
          <span className="capitalize">{event.type}</span>
          {event.duration && (<><Clock className="w-3 h-3" />{event.duration}</>)}
          {event.attendees && (<><Users className="w-3 h-3" />{event.attendees.length}</>)}
        </span>
      </span>
    </button>
  );
};

  return (
    <div className="space-y-5 masira-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('Calendar')}</h1>
          <p className="text-muted-foreground">{t('Manage meetings, tasks, and milestones')}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-secondary rounded-lg p-1" role="tablist" aria-label={t('Calendar view')}>
            {(['month', 'week'] as ViewMode[]).map((m) => (
              <button
                key={m}
                role="tab"
                aria-selected={viewMode === m}
                onClick={() => setViewMode(m)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors capitalize',
                  viewMode === m ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m}
              </button>
            ))}
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 me-2" />
                
                {t('Add Event')}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{t('Create New Event')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddEvent} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">{t('Title')}</Label>
                  <Input id="title" name="title" placeholder={t('Event title')} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">{t('Type')}</Label>
                  <Select name="type" defaultValue="meeting">
                    <SelectTrigger>
                      <SelectValue placeholder={t('Select type')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meeting">{t('Meeting')}</SelectItem>
                      <SelectItem value="task">{t('Task')}</SelectItem>
                      <SelectItem value="milestone">{t('Milestone')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">{t('Date')}</Label>
                    <Input id="date" name="date" type="date" required defaultValue={format(selectedDate, 'yyyy-MM-dd')} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time">{t('Time')}</Label>
                    <Input id="time" name="time" type="time" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t('Description')}</Label>
                  <Textarea id="description" name="description" placeholder={t('Event details...')} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    
                    {t('Cancel')}
                  </Button>
                  <Button type="submit">{t('Create Event')}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
        {/* Calendar */}
        <div className="masira-card !p-0 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-foreground tabular-nums">{tx(title)}</h2>
              <Button variant="outline" size="sm" onClick={goToToday}>
                
                {t('Today')}
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={goToPrevious} aria-label={t('Previous')}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={goToNext} aria-label={t('Next')}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7 border-t border-border">
            {weekDays.map((day) => (
              <div
                key={day}
                className="py-2 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 border-t border-border">
            {days.map((day, index) => {
              const events = getEventsForDate(day);
              const today = isToday(day);
              const selected = isSameDay(day, selectedDate);
              const outside = viewMode === 'month' && !isSameMonth(day, currentDate);

              return (
                <div
                  key={day.toISOString()}
                  role="button"
                  tabIndex={0}
                  aria-label={format(day, 'EEEE, MMMM d') + (events.length ? `, ${events.length} events` : '')}
                  aria-pressed={selected}
                  onClick={() => setSelectedDate(day)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedDate(day);
                    }
                  }}
                  className={cn(
                    'relative p-1.5 sm:p-2 cursor-pointer transition-colors border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                    viewMode === 'week' ? 'min-h-[260px]' : 'min-h-[84px] sm:min-h-[104px]',
                    index % 7 !== 6 && 'border-e',
                    index < days.length - 7 && 'border-b',
                    selected ? 'bg-primary/5' : 'hover:bg-secondary/40',
                    outside && 'bg-secondary/20',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs font-medium tabular-nums',
                      today ? 'bg-primary text-primary-foreground' : outside ? 'text-muted-foreground/60' : 'text-foreground',
                      selected && !today && 'ring-1 ring-primary/50',
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {events.slice(0, maxChips).map((event) => (
                      <button
                        key={event.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEvent(event);
                        }}
                        title={`${event.title} - ${STATUS_STYLE[event.status].label}`}
                        className={cn(
                          'w-full flex items-center gap-1 rounded-sm border-s-2 px-1 py-0.5 text-start text-[11px] leading-tight text-foreground hover:brightness-95 transition',
                          STATUS_STYLE[event.status].chip,
                        )}
                      >
                        <span className="shrink-0 text-muted-foreground">{typeIcon(event.type)}</span>
                        <span className={cn('truncate', event.status === 'done' && 'line-through')}>{tx(event.title)}</span>
                      </button>
                    ))}
                    {events.length > maxChips && (
                      <p className="px-1 text-[11px] text-muted-foreground">+{events.length - maxChips}  {t('more')}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border px-5 py-3 text-xs text-muted-foreground">
            {legend.map((l) => (
              <span key={l.l} className="flex items-center gap-1.5">
                <span className={cn('h-2 w-2 rounded-full', STATUS_STYLE[l.s].dot, l.s === 'done' && 'opacity-60')} />
                {l.l}
              </span>
            ))}
            <span className="ms-auto hidden sm:flex items-center gap-3">
              <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{t('Meeting')}</span>
              <span className="flex items-center gap-1"><CheckSquare className="w-3 h-3" />{t('Task')}</span>
              <span className="flex items-center gap-1"><Target className="w-3 h-3" />{t('Milestone / deadline')}</span>
            </span>
          </div>
        </div>

        {/* Agenda */}
        <aside className="masira-card space-y-5">
          <section>
            <div className="mb-2 flex items-baseline justify-between">
              <h3 className="font-semibold text-foreground">
                {isToday(selectedDate) ? t('Today') : format(selectedDate, 'EEEE')}
              </h3>
              <span className="text-sm text-muted-foreground">{format(selectedDate, 'MMM d')}</span>
            </div>
            {selectedEvents.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                
                {t('Nothing scheduled')}
              </p>
            ) : (
              <div className="-mx-1 space-y-0.5">
                {selectedEvents.map((e) => <AgendaRow key={e.id} event={e} />)}
              </div>
            )}
          </section>

          {upcoming.length > 0 && (
            <section>
              <h3 className="mb-2 font-semibold text-foreground">{t('Coming up')}</h3>
              <div className="-mx-1 space-y-0.5">
                {upcoming.map((e) => (
                  <div key={e.id}>
                    <p className="px-3 pt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                      {format(new Date(e.date), 'EEE, MMM d')}
                    </p>
                    <AgendaRow event={e} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>

      {/* Event Detail Dialog */}
      {selectedEvent && (
        <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <div className={cn('w-3 h-3 rounded', STATUS_STYLE[selectedEvent.status].dot)} />
                {tx(selectedEvent.title)}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3 text-sm">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <span>
                  {new Date(selectedEvent.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
              {selectedEvent.duration && (
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{selectedEvent.duration}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <span className={cn('px-2 py-1 rounded text-xs font-medium', STATUS_STYLE[selectedEvent.status].badge)}>
                  {tx(STATUS_STYLE[selectedEvent.status].label)}
                </span>
                <span className="text-muted-foreground capitalize">{selectedEvent.type}</span>
                {selectedEvent.project && (
                  <span className="text-muted-foreground">{selectedEvent.project}</span>
                )}
              </div>
              {selectedEvent.attendees && selectedEvent.attendees.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-2">{t('Attendees')}</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedEvent.attendees.map((attendee, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-secondary/50 rounded-full px-3 py-1">
                        <Avatar className="w-5 h-5">
                          <AvatarImage src={attendeeAvatars[attendee]} alt={attendee} />
                          <AvatarFallback className="text-[8px]">
                            {attendee.split(' ').map((n) => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{attendee}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSelectedEvent(null)}>
                  
                  {t('Close')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CalendarView;
